import { Alert, Badge, Card, Divider, Group, List, Stack, Text, Title } from "@mantine/core";
import { IconCheck, IconHelpCircle } from "@tabler/icons-react";
import type { Conclusion, Solution } from "../api/types";

interface ConclusionPanelProps {
  conclusion: Conclusion;
  solutions: Solution[];
}

/**
 * Финальный вывод системы — большой блок сверху результатов. По задумке
 * пользователя: «в момент завершения система должна сказать "при таких
 * вводных я считаю вот так. Доклад окончил!"».
 *
 * Окончательный (`final`) — зелёный, уверенный тон.
 * Промежуточный (`preliminary`) — жёлтый, перечислены missing_inputs.
 */
export function ConclusionPanel({ conclusion, solutions }: ConclusionPanelProps) {
  const isFinal = conclusion.confidence_level === "final";
  const top = solutions[conclusion.top_solution_index];

  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      style={{
        borderColor: isFinal ? "var(--mantine-color-teal-5)" : "var(--mantine-color-yellow-5)",
        borderWidth: 2,
      }}
    >
      <Stack gap="sm">
        <Group gap="sm">
          {isFinal ? (
            <IconCheck size={24} color="var(--mantine-color-teal-7)" />
          ) : (
            <IconHelpCircle size={24} color="var(--mantine-color-yellow-7)" />
          )}
          <Title order={3} c={isFinal ? "teal.7" : "yellow.8"}>
            {isFinal ? "Окончательный вывод" : "Промежуточный вывод"}
          </Title>
          <Badge
            variant="light"
            color={isFinal ? "teal" : "yellow"}
            ml="auto"
          >
            Решение #{conclusion.top_solution_index + 1}
          </Badge>
        </Group>

        <Text fw={500} size="md">
          {conclusion.action_summary}
        </Text>

        <Stack gap={4}>
          <Text size="sm" fw={600} c="dimmed">
            Первый шаг
          </Text>
          <Text size="sm">{conclusion.first_step}</Text>
        </Stack>

        {top && (
          <Stack gap={4}>
            <Text size="sm" fw={600} c="dimmed">
              Полное описание решения
            </Text>
            <Text size="sm" c="dimmed">
              {top.description}
            </Text>
          </Stack>
        )}

        {conclusion.alternatives.length > 0 && (
          <>
            <Divider />
            <Stack gap={4}>
              <Text size="sm" fw={600} c="dimmed">
                Альтернативы — близкие по силе или с другим механизмом
              </Text>
              <List size="sm" spacing={4}>
                {conclusion.alternatives.map((alt) => (
                  <List.Item key={alt.solution_index}>
                    <Text component="span" fw={600}>
                      Решение #{alt.solution_index + 1}:
                    </Text>{" "}
                    {alt.why_relevant}
                  </List.Item>
                ))}
              </List>
            </Stack>
          </>
        )}

        <Divider />

        <Text size="xs" c="dimmed">
          <Text component="span" fw={600}>
            Уверенность:
          </Text>{" "}
          {conclusion.confidence_reasoning}
        </Text>

        {!isFinal && conclusion.missing_inputs.length > 0 && (
          <Alert color="yellow" variant="light" title="Чтобы вывод стал окончательным — нужны уточнения">
            <List size="sm">
              {conclusion.missing_inputs.map((inp) => (
                <List.Item key={inp}>{inp}</List.Item>
              ))}
            </List>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
